import React, { useState, useEffect } from 'react';
import TagBadge from './TagBadge';

/**
 * UserTagAssignment
 * 
 * Responsibility: Manage tag assignments for a single user
 * Single Responsibility: User-specific tag assignment UI
 * 
 * @param {string} userId - User ID being edited
 * @param {Array} userTags - Current tags assigned to user
 * @param {Array} availableTags - All available tags
 * @param {Function} onSave - Callback (userId, tagIds) => Promise<void>
 * @param {Function} onCancel - Callback to cancel editing
 */
export default function UserTagAssignment({ userId, userTags, availableTags, onSave, onCancel }) {
    const [selectedTagIds, setSelectedTagIds] = useState(new Set());
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    // Initialize with current tags
    useEffect(() => {
        setSelectedTagIds(new Set(userTags.map(t => t.tag_id)));
    }, [userTags]);

    const handleTagToggle = (tagId) => {
        const newSelection = new Set(selectedTagIds);
        if (newSelection.has(tagId)) {
            newSelection.delete(tagId);
        } else {
            newSelection.add(tagId);
        }
        setSelectedTagIds(newSelection);
    };

    const handleSave = async () => {
        setSaving(true);
        setError('');
        try {
            await onSave(userId, Array.from(selectedTagIds));
            // onSave succeeded — parent will close modal; reset state in case it doesn't
            setSaving(false);
        } catch (err) {
            console.error('[UserTagAssignment] save failed:', err);
            setError(err?.message || 'Failed to save tags');
            setSaving(false);
        }
    };

    const hasChanges = () => {
        const currentIds = new Set(userTags.map(t => t.tag_id));
        if (currentIds.size !== selectedTagIds.size) return true;
        for (const id of currentIds) {
            if (!selectedTagIds.has(id)) return true;
        }
        return false;
    };

    return (
        <div style={styles.container}>
            {error && <div style={styles.error}>{error}</div>}

            <div style={styles.tagGrid}>
                {availableTags.map((tag) => {
                    const isSelected = selectedTagIds.has(tag.tag_id);
                    return (
                        <button
                            key={tag.tag_id}
                            onClick={() => handleTagToggle(tag.tag_id)}
                            disabled={saving}
                            style={{
                                ...styles.tagButton,
                                ...(isSelected && styles.tagButtonSelected),
                                ...(saving && styles.tagButtonDisabled),
                            }}
                            aria-pressed={isSelected}
                        >
                            <span
                                style={{
                                    ...styles.colorDot,
                                    backgroundColor: tag.tag_color,
                                }}
                            />
                            {tag.tag_name}
                            {isSelected && <span style={styles.checkmark}>✓</span>}
                        </button>
                    );
                })}
            </div>

            <div style={styles.actions}>
                <button
                    style={styles.cancelButton}
                    onClick={onCancel}
                    disabled={saving}
                >
                    Cancel
                </button>
                <button
                    style={{
                        ...styles.saveButton,
                        ...((!hasChanges() || saving) && styles.saveButtonDisabled),
                    }}
                    onClick={handleSave}
                    disabled={!hasChanges() || saving}
                >
                    {saving ? 'Saving...' : 'Save Tags'}
                </button>
            </div>
        </div>
    );
}

const styles = {
    container: {
        padding: 12,
        backgroundColor: '#f9fafb',
        borderRadius: 4,
        border: '1px solid #e5e7eb',
    },
    error: {
        backgroundColor: '#fee2e2',
        color: '#991b1b',
        padding: 8,
        borderRadius: 4,
        marginBottom: 12,
        fontSize: 13,
    },
    tagGrid: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        marginBottom: 12,
    },
    tagButton: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '6px 12px',
        backgroundColor: '#fff',
        border: '1px solid #d1d5db',
        borderRadius: 4,
        fontSize: 13,
        cursor: 'pointer',
        transition: 'all 0.15s',
    },
    tagButtonSelected: {
        backgroundColor: '#dbeafe',
        borderColor: '#3b82f6',
        fontWeight: 500,
    },
    tagButtonDisabled: {
        opacity: 0.5,
        cursor: 'not-allowed',
    },
    colorDot: {
        width: 12,
        height: 12,
        borderRadius: '50%',
        border: '1px solid rgba(0,0,0,0.1)',
    },
    checkmark: {
        marginLeft: 2,
        color: '#3b82f6',
        fontWeight: 'bold',
    },
    actions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 8,
    },
    cancelButton: {
        padding: '6px 12px',
        backgroundColor: '#fff',
        color: '#374151',
        border: '1px solid #d1d5db',
        borderRadius: 4,
        cursor: 'pointer',
        fontSize: 13,
    },
    saveButton: {
        padding: '6px 12px',
        backgroundColor: '#3b82f6',
        color: '#fff',
        border: 'none',
        borderRadius: 4,
        cursor: 'pointer',
        fontSize: 13,
        fontWeight: 500,
    },
    saveButtonDisabled: {
        opacity: 0.5,
        cursor: 'not-allowed',
    },
};
